import { useCallback, useMemo, useState } from 'react';
import { Button, FormField, Header, Text, TextInput, VStack } from '@direct-frontend/components';
import { Form, useField, useFormInstance, useFormState } from '@adv-frontend/form-kit';
import { useFormFieldErrorProps } from '#shared/lib/form';
import { InfoBlock } from '#shared/ui/InfoBlock';
import { startEmailLogin } from '../api/emailAuthApi';
import { getEmailAuthErrorMessage } from '../lib/getEmailAuthErrorMessage';
import { EMAIL_AUTH_FORM_ID, emailAuthFieldGetErrorMessage, emailAuthForm } from '../model/emailAuthForm';

const INITIAL_VALUE = { email: '' };

export interface EmailAuthFormProps {
    onSubmit?: (email: string) => Promise<void>;
}

export const EmailAuthForm = ({ onSubmit = startEmailLogin }: EmailAuthFormProps) => {
    const [sentEmail, setSentEmail] = useState<string>();
    const [submitError, setSubmitError] = useState<string>();

    const handleSubmit = useCallback(
        async (email: string) => {
            setSubmitError(undefined);

            try {
                await onSubmit(email);
                setSentEmail(email);
            } catch (error) {
                setSubmitError(getEmailAuthErrorMessage(error));
            }
        },
        [onSubmit]
    );

    const context = useMemo(() => {
        return { onSubmit: handleSubmit };
    }, [handleSubmit]);

    if (sentEmail) {
        return (
            <VStack gap="20" data-testid="EmailAuthForm.Success">
                <VStack gap="8">
                    <Header level="h2">Проверьте почту</Header>
                    <Text>
                        Отправили ссылку для входа на {sentEmail}. Она действует 30 минут и подходит только для одного
                        входа.
                    </Text>
                </VStack>
                <Button
                    color="normal"
                    size="m"
                    onClick={() => {
                        setSentEmail(undefined);
                    }}
                >
                    Указать другую почту
                </Button>
            </VStack>
        );
    }

    return (
        <Form form={emailAuthForm} id={EMAIL_AUTH_FORM_ID} initialValue={INITIAL_VALUE} context={context}>
            <EmailAuthFormContent submitError={submitError} />
        </Form>
    );
};

const EmailAuthFormContent = ({ submitError }: { submitError?: string }) => {
    const { fields, submit } = useFormInstance({ form: emailAuthForm, id: EMAIL_AUTH_FORM_ID });
    const { isSubmitting } = useFormState({ form: emailAuthForm, id: EMAIL_AUTH_FORM_ID });
    const emailField = useField(fields.email);
    const emailErrorProps = useFormFieldErrorProps(emailField.state, emailAuthFieldGetErrorMessage);

    return (
        <VStack gap="20" data-testid="EmailAuthForm">
            <VStack gap="8">
                <Header level="h2">Вход в кабинет</Header>
                <Text>Введите почту, которая указана в настройках доступа к кабинету.</Text>
            </VStack>
            <FormField label="Почта" size="s" {...emailErrorProps}>
                {(controlProps) => (
                    <TextInput
                        {...controlProps}
                        {...emailField.getProps()}
                        aria-label="Почта"
                        inputMode="email"
                        isDisabled={isSubmitting}
                        placeholder="name@example.com"
                        size="m"
                    />
                )}
            </FormField>
            {submitError && (
                <InfoBlock color="error" type="text" data-testid="EmailAuthForm.Error">
                    {submitError}
                </InfoBlock>
            )}
            <Button
                color="accent"
                size="m"
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                onClick={() => void submit()}
                data-testid="EmailAuthForm.Submit"
            >
                Получить ссылку для входа
            </Button>
        </VStack>
    );
};
